import { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { FontSizes, FontWeights, Text } from '@calm-web/design-system';

import Image from '@/components/image/NextImage';
import { useAnalytics } from '@/hooks/analytics';
import { useDeviceState, useUserState } from '@/hooks/store';
import { useIsMobile } from '@/hooks/ui/useIsMobile';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import AppleStore from 'icons/apple-store.svg';
import DownloadGooglePlay from 'icons/download-google-play.svg';

import Header from '../header/Header';
import messages from './messages';
import {
	AppDownloadContainer,
	AppDownloadItemContainer,
	AppImageWrapper,
	CancelInstructions,
	CancelLink,
	ContentContainer,
	IconButton,
	PremiumConfirmationContainer,
	QRCode,
	QRCodeWrapper,
	SocialWrapper,
	Subheading,
	WelcomeContainer,
} from './styles';

export const lifetimeCancelLinks = {
	en: 'https://calmhelp.zendesk.com/hc/en-us/articles/360037871774',
	es: 'https://support.calm.com/hc/es-419/articles/360037871774',
	fr: 'https://support.calm.com/hc/fr/articles/360037871774',
	de: 'https://support.calm.com/hc/de/articles/360037871774',
} as const;
export const isLifetimeCancelLinkLocale = (value: string): value is keyof typeof lifetimeCancelLinks =>
	value in lifetimeCancelLinks;

const PremiumConfirmationV2 = () => {
	const user = useUserState();
	const { formatMessage, locale } = useIntl();
	const routerPush = useRouterPush();
	const device = useDeviceState();
	const { logEvent } = useAnalytics();
	const AppStore = device.isMobile ? (device.os === 'AndroidOS' ? DownloadGooglePlay : AppleStore) : null;
	const [isMobile] = useIsMobile();

	const lifetimeCancelLink = isLifetimeCancelLinkLocale(locale)
		? lifetimeCancelLinks[locale]
		: lifetimeCancelLinks.en;

	const isLifetimeUser = user?.subscription?.is_lifetime;

	const hideQRCode = {
		mobile: true,
		desktop: false,
	};

	function renderTitle() {
		if (isLifetimeUser) {
			return formatMessage(messages.lifetimeWelcome);
		}
		if (user?.subscription?.type === 'voucher') {
			return formatMessage(messages.nonPremiumWelcome);
		}
		return formatMessage(messages.handoffHeader);
	}

	async function goToAppStore() {
		logEvent({
			eventName: `${device.os} : Install : Clicked`,
			eventProps: {
				platform: device?.os || null,
			},
		});
		await routerPush('https://calm.onelink.me/Pf5F/oislygnz');
	}

	async function handleHeaderExit() {
		logEvent({
			eventName: 'Premium Confirmation : Exited',
		});
		await routerPush('/app');
	}

	return (
		<PremiumConfirmationContainer>
			<Header
				hideForBusinessCta
				isStatic
				hideProfile
				hideHambugerNav
				hideAuthCTA
				hideFreeTrialCta
				onExit={handleHeaderExit}
			/>
			<ContentContainer>
				<WelcomeContainer>
					<Text
						role="alert"
						aria-live="polite"
						el="h1"
						size={FontSizes['2xl']}
						weight={FontWeights.Medium}
						align="center"
					>
						{renderTitle()}
					</Text>
					<Subheading align="center" el="p" size={FontSizes.lg} weight={FontWeights.Regular}>
						{formatMessage(messages.subHeading)}
					</Subheading>
					{isLifetimeUser && (
						<CancelInstructions>
							{formatMessage(messages.lifetimeCancelCTA, {
								cancellink: (chunks: ReactNode) => (
									<CancelLink href={lifetimeCancelLink} target="_blank" rel="noopener noreferrer">
										{chunks}
									</CancelLink>
								),
							})}
						</CancelInstructions>
					)}
					{AppStore && (
						<IconButton
							backgroundColor="transparent"
							aria-label={formatMessage(messages.toAppStore)}
							onPress={goToAppStore}
						>
							<AppStore />
						</IconButton>
					)}
				</WelcomeContainer>
				<AppDownloadContainer>
					<AppDownloadItemContainer isHidden={isMobile ? hideQRCode.mobile : hideQRCode.desktop}>
						<QRCodeWrapper>
							<Text align="center" el="h2" size={FontSizes.lg} weight={FontWeights.Medium}>
								{formatMessage(messages.qrCodeInstructions)}
							</Text>
							<QRCode>
								<Image
									src={'/_n/images/onboarding/handoff_skipped_qr.png'}
									alt="Scan QR code to download Calm app"
									height={148}
									width={148}
								/>
							</QRCode>
							<SocialWrapper>
								<AppleStore />
								<DownloadGooglePlay />
							</SocialWrapper>
						</QRCodeWrapper>
					</AppDownloadItemContainer>
					<AppDownloadItemContainer>
						<AppImageWrapper>
							<Image fill src={'/_n/images/dtc-partners/spotify-handoff-device.png'} alt="" />
						</AppImageWrapper>
					</AppDownloadItemContainer>
				</AppDownloadContainer>
			</ContentContainer>
		</PremiumConfirmationContainer>
	);
};

export default PremiumConfirmationV2;
